import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getSimpleUsers } from '@go-client/lib/api/user';
import { canHostEvents } from '@go-shared/lib/permissions';
import { isReservableEvent, snapshotFromEventTemplate } from '@go-shared/lib/reserve';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { NewReservation, Reservation } from '@go-shared/types/reservation';
import { ReserveTimelineItem } from '@go-shared/types/reserve';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import { SimpleUser } from '@go-shared/types/user';
import { FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { BubbleForm, BubbleOverlay } from '../BubbleUI';
import { useBubbleDrawerTools } from '../BubbleUI/BubbleDrawer';
import { BubbleFormPage } from '../BubbleUI/BubbleForm';
import { buildFormik, resetFormik, ReservationFormikForm } from '../Formik/ReservationFormik';
import buildCreateReservationWorkflow from './Workflows/CreateReservationWorkflow';
import buildEditReservationWorkflow from './Workflows/EditReservationWorkflow';
import { ObjectId } from 'bson';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

export interface ReservationWorkflowPrereqs {
  mode: ReservationFormMode;
  formik: FormikProps<ReservationFormikForm>;
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  selectedEventSnapshot?: ReservableEventTemplateSnapshot;
  setSelectedEventSnapshot: (eventSnapshot: ReservableEventTemplateSnapshot) => void;
  onRefreshReservation?: (reservation: Reservation) => void;
  onNavigateToReservation?: (reservation: Reservation) => void;
  onClose: () => void;
}

export interface ResPathDataBundle {
  users: SimpleUser[];
}

export interface ReservationFormModeBase {}

export interface ReservationFormModeCreate {
  type: 'create';
  edit?: Reservation;
  slot?: ReserveTimelineItem;
  event?: BareEventTemplate;
  initialDate?: string;
}

export interface ReservationFormModeEdit {
  type: 'edit';
  edit: Reservation;
  goToPayments?: boolean;
}

export type ReservationFormMode = ReservationFormModeBase &
  (ReservationFormModeCreate | ReservationFormModeEdit);

export interface ReservationPathProps {
  users?: SimpleUser[];
  mode: ReservationFormMode;
  onClose: () => void;
  onRefreshReservation?: (reservation: Reservation) => void;
  onNavigateToReservation?: (reservation: Reservation) => void;
}

export default function ReservationPath(props: ReservationPathProps) {
  const { mode } = props;

  const { serverDate } = usePublicServerInfo();
  const { activeProperty } = useActiveProperty();
  const [dataBundle, setDataBundle] = useState<ResPathDataBundle | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  const { setVerifyClose } = useBubbleDrawerTools();

  const [selectedEventSnapshot, setSelectedEventSnapshot] = useState<
    ReservableEventTemplateSnapshot | undefined
  >(
    mode.type === 'edit'
      ? mode.edit.eventSnapshot
      : isReservableEvent(mode.event)
      ? snapshotFromEventTemplate(mode.event)
      : undefined
  );

  useEffect(() => {
    initialize().catch(console.error);
  }, [mode.edit]);

  async function initialize() {
    const users = props.users || (await getSimpleUsers(activeProperty._id));

    setDataBundle({ users: users.filter(canHostEvents) });
  }

  async function onCreateFormSubmit(values: ReservationFormikForm): Promise<NewReservation> {
    if (!selectedEventSnapshot) {
      throw new Error('An event template must be selected before attempting to submit!');
    }

    if (!values.time) {
      throw new Error('A time must be selected before attempting to submit!');
    }

    const additionalOccurrences = values.additionalOccurrences ?? ([] as const);

    if (additionalOccurrences.some(ao => !ao.time)) {
      throw new Error('All occurrences must have a time selected before attempting to submit!');
    }

    const newReservation: NewReservation = {
      _id: new ObjectId().toHexString(),
      property: activeProperty._id,
      eventTemplate: selectedEventSnapshot.eventTemplate,
      date: new Date(values.date),
      time: values.time,
      timeDisplay: values.timeDisplay,
      duration: values.duration,
      host: values.host,
      rental: values.rental,
      fields: values.fields,
      customer: {
        firstName: values.customer.firstName,
        lastName: values.customer.lastName,
        email: values.customer.email,
        phone: values.customer.phone,
      },
      tickets: values.tickets.map(t => ({
        _id: t._id,
        ticketId: t.ticketId,
        values: t.values,
      })),
      packages: values.packages.map(p => ({
        _id: p._id,
        packageId: p.packageId,
        quantity: 1,
      })),
      addons: values.addons
        .map(a => ({
          _id: a._id,
          addonId: a.addonId,
          quantity: a.quantity,
        }))
        .filter(a => a.quantity > 0),
      priceOverrides: values.priceOverrides,
      additionalOccurrences: additionalOccurrences.map(({ _id: _ignored, ...ao }) => ({
        ...ao,
        _id: new ObjectId().toHexString(),
        time: ao.time!,
        date: new Date(ao.date),
      })),
    };

    return newReservation;
  }

  let slot: ReserveTimelineItem | undefined = undefined;
  let reservation: Reservation | undefined = undefined;
  let initialDate = serverDate.iso;

  if (mode.type === 'create') {
    slot = mode.slot;
    reservation = mode.edit;
    if (mode.initialDate) initialDate = mode.initialDate;
  } else if (mode.type === 'edit') {
    reservation = mode.edit;
  }

  const formik = buildFormik(
    initialDate,
    slot,
    reservation,
    selectedEventSnapshot,
    onCreateFormSubmit
  );

  useEffect(() => {
    setVerifyClose(formik.dirty);
  }, [formik.dirty]);

  useEffect(() => {
    resetFormik(initialDate, formik, slot, reservation, selectedEventSnapshot);
  }, [mode, selectedEventSnapshot, initialDate]);

  let startAtPage: number | undefined;
  const preqs: ReservationWorkflowPrereqs = {
    formik,
    mode,
    isBusy,
    setIsBusy,
    selectedEventSnapshot,
    setSelectedEventSnapshot,
    onClose: props.onClose,
    onRefreshReservation: props.onRefreshReservation,
    onNavigateToReservation: props.onNavigateToReservation,
  };

  const pages: Array<BubbleFormPage<ResPathDataBundle>> = [];

  switch (mode.type) {
    case 'edit':
      buildEditReservationWorkflow(pages, preqs);
      startAtPage = 0;
      break;
    case 'create':
      buildCreateReservationWorkflow(pages, preqs);
      if (mode.event) startAtPage = 1;
      break;
  }

  return (
    <div className="reservation-path-form">
      <BubbleOverlay loading={!dataBundle} hideBg style={{ minHeight: '500px' }}>
        {dataBundle && (
          <BubbleForm pages={pages} startAtPage={startAtPage} resources={dataBundle} />
        )}
      </BubbleOverlay>
    </div>
  );
}
